import publicSeo from "@/seo/publicSeo.json";
import { findPublishedArticle, publishedArticlesForLocale } from "@/lib/blog";

export const SITE_NAME = publicSeo.site.name;
export const SITE_ORIGIN = publicSeo.site.origin;
export const DEFAULT_SOCIAL_IMAGE = `${SITE_ORIGIN}${publicSeo.site.defaultImage}`;
const ORGANIZATION_LOGO = `${SITE_ORIGIN}${publicSeo.site.logo}`;

const absoluteUrl = (value) => {
  if (!value) return null;
  return value.startsWith("http") ? value : `${SITE_ORIGIN}${value}`;
};

export function normalizePublicPath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

export function publicRoutePath(pageKey, locale = "es") {
  return Object.entries(publicSeo.routes).find(([, route]) => route.pageKey === pageKey && route.locale === locale)?.[0] || null;
}

export function publicLocaleFromPath(pathname) {
  const path = normalizePublicPath(pathname);
  const routeLocale = publicSeo.routes[path]?.locale;
  if (routeLocale) return routeLocale;
  if (/^\/en(?:\/|$)/.test(path)) return "en";
  if (/^\/(?:blog|herramientas)(?:\/|$)/.test(path) || ["/", "/precios", "/equipo-editorial"].includes(path)) return "es";
  return null;
}

function localeSettings(locale) {
  return publicSeo.site.locales[locale] || publicSeo.site.locales[publicSeo.site.defaultLocale];
}

function alternateLinks(locale, path, alternatePath) {
  if (!alternatePath) return [];
  const alternateLocale = locale === "en" ? "es" : "en";
  const localized = {
    [locale]: path,
    [alternateLocale]: alternatePath,
  };
  const spanishPath = localized.es;
  return ["es", "en"].map((hreflang) => ({ hreflang, href: `${SITE_ORIGIN}${localized[hreflang]}` }))
    .concat({ hreflang: "x-default", href: `${SITE_ORIGIN}${spanishPath}` });
}

const organization = () => ({
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: SITE_NAME,
  url: SITE_ORIGIN,
  logo: { "@type": "ImageObject", url: ORGANIZATION_LOGO, width: 440, height: 440 },
});

const localizedHomeUrl = (locale) => {
  const homePath = publicRoutePath("home", locale);
  return !homePath || homePath === "/" ? SITE_ORIGIN : `${SITE_ORIGIN}${homePath}`;
};

const website = (locale) => {
  const url = localizedHomeUrl(locale);
  return {
    "@type": "WebSite",
    "@id": `${url}#website`,
    name: SITE_NAME,
    url,
    inLanguage: localeSettings(locale).language,
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  };
};

const platformApplication = (locale) => {
  const application = publicSeo.site.application;
  const localized = application.locales[locale] || application.locales[publicSeo.site.defaultLocale];
  const homeUrl = localizedHomeUrl(locale);
  return {
    "@type": "WebApplication",
    "@id": `${homeUrl}${homeUrl === SITE_ORIGIN ? "/" : ""}#application`,
    name: localized.name,
    description: localized.description,
    url: homeUrl,
    applicationCategory: application.applicationCategory,
    operatingSystem: application.operatingSystem,
    featureList: localized.featureList,
    offers: localized.offers.map(({ urlPath, ...offer }) => ({
      "@type": "Offer",
      ...offer,
      url: absoluteUrl(urlPath),
      availability: "https://schema.org/OnlineOnly",
    })),
    inLanguage: localeSettings(locale).language,
    provider: { "@id": `${SITE_ORIGIN}/#organization` },
  };
};

const blogCollection = (path, route) => {
  const url = `${SITE_ORIGIN}${path}`;
  const listId = `${url}#articles`;
  const articles = [...publishedArticlesForLocale(route.locale)]
    .sort((left, right) => left.order - right.order || Date.parse(right.publishedAt) - Date.parse(left.publishedAt));
  return [
    {
      "@type": "CollectionPage",
      "@id": `${url}#page`,
      name: route.h1,
      description: route.description,
      url,
      isPartOf: { "@id": `${localizedHomeUrl(route.locale)}#website` },
      mainEntity: { "@id": listId },
      inLanguage: localeSettings(route.locale).language,
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    },
    {
      "@type": "ItemList",
      "@id": listId,
      name: route.collectionName || route.h1,
      numberOfItems: articles.length,
      itemListElement: articles.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "BlogPosting",
          "@id": `${SITE_ORIGIN}${article.path}#article`,
          headline: article.title,
          description: article.description,
          url: `${SITE_ORIGIN}${article.path}`,
          image: [absoluteUrl(article.image)],
          inLanguage: localeSettings(article.locale).language,
        },
      })),
    },
  ];
};

const breadcrumbs = (article) => {
  const homePath = publicRoutePath("home", article.locale);
  const blogPath = publicRoutePath("blog", article.locale);
  return {
    "@type": "BreadcrumbList",
    "@id": `${SITE_ORIGIN}${article.path}#breadcrumbs`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: article.locale === "en" ? "Home" : "Inicio", item: `${SITE_ORIGIN}${homePath === "/" ? "" : homePath}` },
      { "@type": "ListItem", position: 2, name: article.locale === "en" ? "Guides" : "Guías", item: `${SITE_ORIGIN}${blogPath}` },
      { "@type": "ListItem", position: 3, name: article.title, item: `${SITE_ORIGIN}${article.path}` },
    ],
  };
};

export function buildArticleStructuredData(article) {
  const url = `${SITE_ORIGIN}${article.path}`;
  const blogPosting = {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: article.title,
    description: article.description,
    image: [absoluteUrl(article.image) || DEFAULT_SOCIAL_IMAGE],
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    author: { "@type": article.authorType, name: article.author, url: absoluteUrl(article.authorUrl) },
    reviewedBy: { "@type": "Organization", name: article.reviewer, url: absoluteUrl(article.reviewerUrl) },
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    articleSection: article.category,
    keywords: article.tags,
    inLanguage: localeSettings(article.locale).language,
  };
  return { "@context": "https://schema.org", "@graph": [organization(), website(article.locale), breadcrumbs(article), blogPosting] };
}

function buildStaticStructuredData(path, route) {
  const language = localeSettings(route.locale).language;
  const graph = [organization(), website(route.locale)];
  if (["home", "pricing"].includes(route.pageKey)) graph.push(platformApplication(route.locale));
  if (route.pageKey === "diagnosis") graph.push({
    "@type": "WebApplication",
    "@id": `${SITE_ORIGIN}${path}#diagnosis`,
    name: route.h1,
    description: route.description,
    url: `${SITE_ORIGIN}${path}`,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    inLanguage: language,
    provider: { "@id": `${SITE_ORIGIN}/#organization` },
  });
  if (route.pageKey === "blog") graph.push(...blogCollection(path, route));
  if (route.toolKey) graph.push({
    "@type": "WebApplication",
    "@id": `${SITE_ORIGIN}${path}#tool`,
    name: route.h1,
    description: route.description,
    url: `${SITE_ORIGIN}${path}`,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    inLanguage: language,
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  });
  if (route.pageKey === "editorial") graph.push({
    "@type": "AboutPage",
    "@id": `${SITE_ORIGIN}${path}#page`,
    name: route.h1,
    url: `${SITE_ORIGIN}${path}`,
    about: { "@id": `${SITE_ORIGIN}/#organization` },
    inLanguage: language,
  });
  if (route.person) {
    const personId = `${SITE_ORIGIN}${path}#person`;
    graph.push({
      "@type": "Person",
      "@id": personId,
      name: route.person.name,
      url: `${SITE_ORIGIN}${path}`,
      jobTitle: route.person.jobTitle,
      sameAs: [route.person.sameAs],
    });
    graph.push({
      "@type": "ProfilePage",
      "@id": `${SITE_ORIGIN}${path}#page`,
      name: route.h1,
      url: `${SITE_ORIGIN}${path}`,
      mainEntity: { "@id": personId },
      inLanguage: language,
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

function localizedSeo(seo, locale, path, alternatePath) {
  const settings = localeSettings(locale);
  return {
    ...seo,
    locale,
    htmlLang: settings.htmlLang,
    ogLocale: settings.ogLocale,
    alternatePath,
    alternates: alternateLinks(locale, path, alternatePath),
  };
}

const privateRouteMetadata = [
  [/^\/app$/, { es: ["Panel", "Espacio privado de Guía Emprendedor."], en: ["Dashboard", "Your private Guía Emprendedor workspace."] }],
  [/^\/app\/proyecto$/, { es: ["Mi proyecto", "Datos privados de tu proyecto."], en: ["My project", "Your private project details."] }],
  [/^\/app\/comparacion$/, { es: ["Comparar formas jurídicas", "Comparativa privada de formas jurídicas."], en: ["Compare business structures", "Private business-structure comparison."] }],
  [/^\/app\/itinerario$/, { es: ["Mi itinerario", "Itinerario privado para crear tu empresa."], en: ["My itinerary", "Your private company-creation itinerary."] }],
  [/^\/app\/tramite\/[^/]+$/, { es: ["Detalle del trámite", "Detalle privado de un paso de tu itinerario."], en: ["Step details", "Private details for an itinerary step."] }],
  [/^\/app\/asistente$/, { es: ["Asistente", "Asistente privado de Guía Emprendedor."], en: ["Assistant", "Your private Guía Emprendedor assistant."] }],
  [/^\/app\/documentos$/, { es: ["Documentos", "Documentos privados de tu proyecto."], en: ["Documents", "Your private project documents."] }],
  [/^\/app\/prepara-tu-cita$/, { es: ["Prepara tu cita", "Preparación privada para tus primeras citas profesionales."], en: ["Prepare your meeting", "Private preparation for your first professional meetings."] }],
  [/^\/app\/facturacion$/, { es: ["Facturación", "Facturación privada de tu cuenta."], en: ["Billing", "Your private account billing."] }],
  [/^\/app\/perfil$/, { es: ["Perfil", "Perfil privado de tu cuenta."], en: ["Profile", "Your private account profile."] }],
  [/^\/app\/notificaciones$/, { es: ["Notificaciones", "Notificaciones privadas de tu cuenta."], en: ["Notifications", "Your private account notifications."] }],
  [/^\/app\/(opiniones|ayuda)$/, { es: ["Opiniones y ayuda", "Área privada de comentarios y ayuda."], en: ["Feedback and help", "Private feedback and help area."] }],
  [/^\/app\/contacto(?:-asesor)?$/, { es: ["Contactar con un asesor", "Contacto privado con un asesor."], en: ["Contact an advisor", "Private advisor contact."] }],
  [/^\/app\/admin$/, { es: ["Administración", "Área privada de administración."], en: ["Administration", "Private administration area."] }],
  [/^\/app\/(asesores|asesorias)(?:\/[^/]+)?$/, { es: ["Panel", "Espacio privado de Guía Emprendedor."], en: ["Dashboard", "Your private Guía Emprendedor workspace."] }],
  [/^\/panel-asesor$/, { es: ["Panel de asesor", "Panel privado para asesores."], en: ["Advisor panel", "Private advisor panel."] }],
  [/^\/payment\/success$/, { es: ["Pago confirmado", "Confirmación privada de tu pago."], en: ["Payment confirmed", "Private payment confirmation."] }],
];

const applicationRouteMetadata = [
  [/^\/soporte$/, { es: ["Soporte", "Ayuda de Guía Emprendedor."], en: ["Support", "Guía Emprendedor support."] }],
  [/^\/(login|registro|recuperar|restablecer|verificar|auth\/action)$/, { es: ["Acceso a tu cuenta", "Acceso seguro a Guía Emprendedor."], en: ["Account access", "Secure access to Guía Emprendedor."] }],
  [/^\/diagnostico\/(preguntas|resultado)$/, { es: ["Diagnóstico", "Diagnóstico privado para tu proyecto."], en: ["Diagnosis", "Private diagnosis for your project."] }],
  [/^\/pago$/, { es: ["Elegir un plan", "Acceso al espacio privado de Guía Emprendedor."], en: ["Choose a plan", "Access the private Guía Emprendedor workspace."] }],
];

function knownNoIndexSeo(path, locale) {
  const metadata = [...privateRouteMetadata, ...applicationRouteMetadata]
    .find(([pattern]) => pattern.test(path))?.[1];
  if (!metadata) return null;
  const [title, description] = metadata[locale] || metadata.es;
  return noIndexSeo(path, locale, title, description);
}

export function resolvePublicSeo(pathname, activeLocale = "es") {
  const path = normalizePublicPath(pathname);
  const route = publicSeo.routes[path];
  if (route) return localizedSeo({
    ...route,
    path,
    canonicalPath: path,
    robots: "index, follow",
    image: DEFAULT_SOCIAL_IMAGE,
    imageAlt: `${SITE_NAME}: ${route.h1}`,
    structuredData: buildStaticStructuredData(path, route),
  }, route.locale, path, route.alternatePath);

  const articleMatch = path.match(/^\/(en\/)?blog\/([a-z0-9]+(?:-[a-z0-9]+)*)$/);
  if (articleMatch) {
    const locale = articleMatch[1] ? "en" : "es";
    const article = findPublishedArticle(articleMatch[2], locale);
    if (article) return localizedSeo({
      title: article.seoTitle || article.title,
      description: article.description,
      path,
      canonicalPath: path,
      robots: "index, follow",
      type: "article",
      image: absoluteUrl(article.image) || DEFAULT_SOCIAL_IMAGE,
      imageAlt: article.imageAlt,
      structuredData: buildArticleStructuredData(article),
    }, locale, path, article.alternatePath);
    return noIndexSeo(path, locale, locale === "en" ? "Guide not available" : "Guía no disponible", locale === "en" ? "The requested guide is not published." : "La guía solicitada no está publicada.");
  }

  const locale = publicLocaleFromPath(path) || activeLocale;
  if (path.startsWith("/legal/")) return noIndexSeo(path, locale, "Información legal", "Documentación legal de Guía Emprendedor.");
  const knownRoute = knownNoIndexSeo(path, locale);
  if (knownRoute) return knownRoute;
  return noIndexSeo(path, locale, locale === "en" ? "Page not found" : "Página no encontrada", locale === "en" ? "The requested page does not exist or is unavailable." : "La página solicitada no existe o no está disponible.");
}

function noIndexSeo(path, locale, title, description) {
  return localizedSeo({
    title,
    description,
    path,
    canonicalPath: null,
    robots: "noindex, follow",
    type: "website",
    image: DEFAULT_SOCIAL_IMAGE,
    imageAlt: SITE_NAME,
    structuredData: null,
  }, locale, path, null);
}

export function publicToolSeo(toolKey, locale = "es") {
  return Object.entries(publicSeo.routes).find(([, route]) => route.toolKey === toolKey && route.locale === locale)?.[1] || null;
}

export { publicSeo };
